import { Button } from "@/components/ui/button";
import { ShoppingCart, Menu, Loader2, WrenchIcon, Shield } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Link, useLocation } from "@/components/ui/link";
import AuthDialog from "./AuthDialog";
import { useCart } from "@/context/CartContext";
import { routes, AppRoute } from "@/routes/routes";
import { hasAnyRole, hasRole } from "@/lib/types";
import logoWebP from "@/assets/logonew.webp";
import logoPng from "@/assets/logonew-optimized.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface NavbarProps {
  maintenance?: boolean;
}

const Navbar = ({ maintenance = false }: NavbarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAuthDialogOpen, setIsAuthDialogOpen] = useState(false);
  const { user, userData, isImpersonating, stopImpersonation, signOut } = useAuth();
  const { getItemCount, isLoading } = useCart();
  const location = useLocation();

  const cartCount = getItemCount();

  // Filter routes for main navigation - only show public/non-user-specific routes
  const navRoutes = routes.filter(route => {
    // Skip catch-all route and cart (handled separately)
    if (route.path === "*" || route.path === "/cart") return false;
    // Don't show admin routes in the main navigation
    if (route.adminRequired) return false;
    // Don't show user-specific routes in main navigation (they go in user menu)
    if (route.protected) return false;

    // Only show routes that have navbar: true
    if (route.navbar !== true) return false;

    return true;
  });

  // Helper to check if user can see a route based on required roles
  const canSeeRoute = (route: AppRoute): boolean => {
    // If route has required roles, check if user has any of them
    if (route.requiredRoles && route.requiredRoles.length > 0) {
      return hasAnyRole(userData, route.requiredRoles);
    }
    return true;
  };

  // Filter routes for the user menu (account + other user features)
  const userMenuRoutes = routes.filter(route => {
    // Skip catch-all route
    if (route.path === "*") return false;
    // Only include protected routes
    if (!route.protected) return false;
    // Don't show admin routes in the user menu
    if (route.adminRequired) return false;
    // Exclude workflow-specific routes from user menu
    if (
      route.path === "/projects/new" ||
      route.path === "/cart" ||
      route.path === "/order-confirmation"
    )
      return false;

    // Only show routes that have navbar: true
    if (route.navbar !== true) return false;

    // Check role-based visibility
    if (!canSeeRoute(route)) return false;

    return true;
  });

  const adminRoutes = routes.filter(route => {
    // Skip catch-all route
    if (route.path === "*") return false;
    // Only include protected routes
    if (!route.protected) return false;

    if (!route.adminRequired) return false;
    // Only show admin routes if user is an admin
    if (route.adminRequired && !hasRole(userData, "admin")) return false;
    // Exclude workflow-specific routes from user menu
    if (
      route.path === "/projects/new" ||
      route.path === "/cart" ||
      route.path === "/order-confirmation"
    )
      return false;

    // Only show routes that have navbar: true
    if (route.navbar !== true) return false;

    return true;
  });

  return (
    <nav className="bg-white shadow-sm py-2 px-6 sticky top-0 z-50">
      <div className="container mx-auto flex justify-between items-center">
        <div className="flex items-center overflow-hidden h-14">
          <Link to={"/"} className="flex items-center">
            <picture>
              <source srcSet={logoWebP} type="image/webp" />
              <img
                src={logoPng}
                alt="stickersbyai logo"
                width={400}
                height={267}
                className="h-32 w-auto object-contain drop-shadow-md"
              />
            </picture>
          </Link>
        </div>

        {maintenance ? (
          <div className="flex items-center">
            <div className="rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-sm font-medium flex items-center">
              <WrenchIcon className="h-4 w-4 mr-1" />
              Maintenance Mode
            </div>
          </div>
        ) : (
          <>
            <div className="hidden md:flex items-center space-x-2">
              {!user &&
                navRoutes.map(route => (
                  <Link
                    key={route.path}
                    to={route.path!}
                    className={cn(
                      "px-3 py-2 rounded-md text-sm font-medium transition-colors",
                      location.pathname === route.path
                        ? "bg-primary text-white hover:text-white hover:bg-primary/90"
                        : "text-primary hover:bg-primary/10"
                    )}
                  >
                    {route.title}
                  </Link>
                ))}
              {user && (
                <Link to="/cart">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1 border-accent hover:bg-accent/50 hover:text-primary"
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <ShoppingCart className="h-4 w-4 text-accent/80 hover:text-accent hover:scale-110 transition-all duration-300" />
                    )}
                    <span>Cart ({cartCount})</span>
                  </Button>
                </Link>
              )}
              {user && isImpersonating && (
                <div className="bg-orange-100 text-orange-800 px-3 py-1 rounded-md flex items-center mr-2">
                  <Shield className="h-4 w-4 mr-1" />
                  <span className="text-sm">Impersonating User</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="ml-2 h-6 text-xs"
                    onClick={() => stopImpersonation()}
                  >
                    Exit
                  </Button>
                </div>
              )}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      className="bg-primary text-primary-foreground hover:bg-primary/80 flex items-center gap-1"
                    >
                      <Menu className="h-4 w-4" />
                      <span>Menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {userMenuRoutes.map(route => (
                      <DropdownMenuItem key={route.path} asChild>
                        <Link to={route.path!} className="cursor-pointer">
                          {route.title}
                        </Link>
                      </DropdownMenuItem>
                    ))}

                    {adminRoutes.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                          Admin
                        </div>
                        {adminRoutes.map(route => (
                          <DropdownMenuItem key={route.path} asChild>
                            <Link to={route.path!} className="cursor-pointer">
                              {route.title}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="cursor-pointer text-red-500 hover:bg-red-500 hover:text-white focus:bg-red-500 focus:text-white"
                      onClick={() => signOut()}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                        <polyline points="16 17 21 12 16 7"></polyline>
                        <line x1="21" y1="12" x2="9" y2="12"></line>
                      </svg>
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  size="sm"
                  className="bg-secondary text-white hover:bg-secondary/90"
                  onClick={() => setIsAuthDialogOpen(true)}
                >
                  <span>Sign in</span>
                </Button>
              )}
            </div>

            <div className="md:hidden">
              <div className="flex items-center gap-2">
                {user && (
                  <Link to="/cart">
                    <Button variant="outline" size="sm" className="flex items-center gap-1">
                      {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ShoppingCart className="h-4 w-4" />
                      )}
                      <span>({cartCount})</span>
                    </Button>
                  </Link>
                )}
                {user && isImpersonating && (
                  <div className="bg-orange-100 text-orange-800 px-2 py-1 rounded-md flex items-center">
                    <Shield className="h-3 w-3 mr-1" />
                    <span className="text-xs">Impersonating</span>
                  </div>
                )}
                <Button variant="ghost" size="icon" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                  <span className="sr-only">Open menu</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="4" x2="20" y1="12" y2="12" />
                    <line x1="4" x2="20" y1="6" y2="6" />
                    <line x1="4" x2="20" y1="18" y2="18" />
                  </svg>
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden pt-4 pb-2 px-6 space-y-2">
          {!user &&
            navRoutes.map(route => (
              <Link
                key={route.path}
                to={route.path!}
                className={cn(
                  "block px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  location.pathname === route.path
                    ? "bg-primary text-white hover:text-white hover:bg-primary/90"
                    : "text-primary hover:bg-primary/10"
                )}
              >
                {route.title}
              </Link>
            ))}

          {/* User menu options for mobile - only shown if user is logged in */}
          {user && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              {userMenuRoutes.map(route => (
                <Link
                  key={route.path}
                  to={route.path!}
                  className="block px-3 py-2 rounded-md text-sm font-medium text-primary hover:bg-primary/10"
                >
                  {route.title}
                </Link>
              ))}

              {adminRoutes.length > 0 && (
                <>
                  <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">
                    Admin
                  </div>
                  {adminRoutes.map(route => (
                    <Link
                      key={route.path}
                      to={route.path!}
                      className="block px-3 py-2 rounded-md text-sm font-medium text-primary hover:bg-primary/10"
                    >
                      {route.title}
                    </Link>
                  ))}
                </>
              )}

              {isImpersonating && (
                <button
                  onClick={() => stopImpersonation()}
                  className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-orange-600 hover:bg-orange-50 flex items-center"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Exit Impersonation
                </button>
              )}

              <button
                onClick={() => signOut()}
                className="w-full text-left px-3 py-2 rounded-md text-sm font-medium text-red-500 hover:bg-red-50 flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Sign out
              </button>
            </div>
          )}

          {/* Show sign in button if user is not logged in */}
          {!user && (
            <Button
              size="sm"
              className="w-full mt-2 bg-secondary text-white hover:bg-secondary/90"
              onClick={() => setIsAuthDialogOpen(true)}
            >
              Sign In
            </Button>
          )}
        </div>
      )}

      <AuthDialog isOpen={isAuthDialogOpen} onOpenChange={setIsAuthDialogOpen} />
    </nav>
  );
};

export default Navbar;
